import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayers, queriesPayrollSchedules } from "@query";
import {
  toWSDateString,
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputNumber,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayerPayrollScheduleDate,
  PayrollSchedule,
  PayrollScheduleFrequency,
  PayrollScheduleType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { RadioGroupWithCustomInputs } from "../../../components/RadioGroupWithCustomInputs/RadioGroupWithCustomInputs";
import {
  PayrollCadence,
  selectorPayrollCadence
} from "../../../shared/selectors/selectorPayrollCadence";
import { PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD } from "../../../modules/Settings/paths";
import { EmptyState } from "../../../shared/components/EmptyState";
import { WeekDay } from "../../../shared/types/time";
import {
  dayInIntervalToWeekDayMapping,
  weekDayToDayInIntervalMapping
} from "../../../shared/utils/time";
import { getExactCutOffDate } from "../../utils/payables";

/**
 * Contractor payroll schedule settings, on v3.
 *
 * Two v3 resources back this screen, and they are deliberately not the same one:
 *
 * - **cadence** lives on `PayrollSchedule` (`/v3/payments/payroll-schedules`),
 *   the resource v3 designates for it. `PayerSettings` does not declare a 1099
 *   `frequency` at all, so there is nowhere else to read or write it.
 * - **the upcoming runs and the payroll on/off switch** live on `PayerSettings`
 *   (`/v3/payments/payer-settings`), which projects `payrollSettings.scheduleDates`
 *   read-only and accepts `runsPayroll` on its PATCH.
 *
 * Neither write is confirmed working. The payer-settings PATCH is mounted and
 * validates a well-formed body (an unauthenticated probe reaches 401, while an
 * unmounted verb on the same path still answers 405), so the PLA-3495 write verb
 * has shipped — but whether its handler is implemented cannot be observed from
 * outside, because a 501 is raised behind auth and looks identical to a working
 * endpoint. The cadence resource is documented as 501 on every operation and
 * that is likewise unverifiable without a session.
 *
 * Both are wired to v3 regardless, per the migration rule that a user-triggered
 * action moves even when its endpoint is incomplete: the screen still renders off
 * the live read, and a failed save surfaces at the click through `ErrorAlert`
 * instead of hiding behind a dead v1 call. See V3_MIGRATION_TRACKER.md
 * § payer-settings for the full gap list.
 */

enum Options {
  Monthly = "Monthly",
  SemiMonthly = "SemiMonthly",
  Everyday = "Everyday",
  Week = "Week"
}

type FormData = {
  interval: Options;
  every: number;
  dayInInterval: WeekDay;
};

/**
 * `PayrollScheduleFrequency` has no "every N weeks" member — only `Weekly` and
 * `Biweekly` — so the design's free-number input is representable for N of 1 or
 * 2 and nothing else. Rather than let a payer type 3 and receive a 422 they
 * cannot act on, the form rejects it with the reason. Tracked as gap G-CAD-N.
 */
const MAX_WEEK_INTERVAL = 2;

const weekIntervalToFrequency = (every: number) =>
  every === 1
    ? PayrollScheduleFrequency.WEEKLY
    : PayrollScheduleFrequency.BIWEEKLY;

const WEEK_DAYS = Object.values(WeekDay);

/**
 * `WeekDay` and the `dayInInterval` mappings run Monday-first (1–7), while
 * `Date.getUTCDay()` runs Sunday-first (0–6). Indexing one with the other reads
 * a Friday schedule as Saturday, so every crossing goes through here.
 */
const jsDayToDayInInterval = (jsDay: number) => (jsDay === 0 ? 7 : jsDay);

const dayInIntervalToJsDay = (dayInInterval: number) =>
  dayInInterval === 7 ? 0 : dayInInterval;

/**
 * v3 carries no day-of-week field: the weekday a weekly schedule lands on is
 * implied by `firstPayDate`. Reading the selection back therefore means reading
 * that date's day, and saving one means moving `firstPayDate` onto the next
 * occurrence of the chosen weekday.
 */
const frequencyToFormData = (
  schedule: PayrollSchedule | undefined,
  /**
   * Fallback for when the cadence resource is unavailable — see
   * {@link selectorPayrollCadence}. The schedule wins when both are present,
   * since it is the stored intent rather than a reading of its output.
   */
  derived?: PayrollCadence
): FormData => {
  const frequency = schedule?.frequency ?? derived?.frequency;

  const anchorDay =
    schedule?.firstPayDate !== undefined
      ? new Date(schedule.firstPayDate).getUTCDay()
      : derived?.weekday;

  const dayInInterval =
    anchorDay === undefined
      ? WeekDay.Monday
      : dayInIntervalToWeekDayMapping[jsDayToDayInInterval(anchorDay)];

  switch (frequency) {
    case PayrollScheduleFrequency.DAILY:
      return { interval: Options.Everyday, every: 1, dayInInterval };
    case PayrollScheduleFrequency.SEMI_MONTHLY:
      return { interval: Options.SemiMonthly, every: 1, dayInInterval };
    case PayrollScheduleFrequency.WEEKLY:
      return { interval: Options.Week, every: 1, dayInInterval };
    case PayrollScheduleFrequency.BIWEEKLY:
      return { interval: Options.Week, every: 2, dayInInterval };
    default:
      return { interval: Options.Monthly, every: 1, dayInInterval };
  }
};

/** The next occurrence of `weekDay`, which is how v3 encodes a weekly landing day. */
const nextDateOnWeekDay = (weekDay: WeekDay) => {
  const target = dayInIntervalToJsDay(weekDayToDayInIntervalMapping[weekDay]);
  const date = new Date();
  date.setUTCDate(
    date.getUTCDate() + ((target - date.getUTCDay() + 7) % 7 || 7)
  );
  return date;
};

const ModalDisablePayroll: React.FC<WSModalCustomProps<boolean>> = props => (
  <WSModal
    title="Are you sure?"
    size="S"
    onClose={() => props.onClose(false)}
    actions={{
      buttons: [
        {
          kind: "Tertiary",
          label: "Cancel",
          onClick: () => props.onClose(false)
        },
        {
          kind: "Primary",
          destructive: true,
          label: "Yes, disable payroll",
          onClick: () => props.onClose(true)
        }
      ]
    }}
  >
    <WSAlert
      theme="warning"
      icon="alert-circle"
      title="Disable payroll and remove upcoming payroll runs"
    >
      If you proceed with disabling payroll now, all upcoming payroll runs will
      be removed. You can always re-enable it and set up your payroll runs from
      scratch.
    </WSAlert>
  </WSModal>
);

const useModalDisablePayroll = () => useWSModalCustom(ModalDisablePayroll);

export const PayrollSettings: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();
  const modalDisablePayroll = useModalDisablePayroll();

  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const querySchedules = queriesPayrollSchedules.listPayrollSchedules.useQuery(
    { filter: { type: { eq: PayrollScheduleType.CONTRACTOR_VENDOR } } },
    // The cadence resource is unimplemented, and a 501 never becomes a 200 — so
    // retrying it just delays the point at which the form can admit it has no
    // saved cadence to show, while spending five requests to learn nothing.
    { retry: false }
  );

  const [updatePayerSettings, updatePayerSettingsMeta] =
    queriesPayers.updateCurrentPayerSettings.useMutation();
  const [updateSchedule, updateScheduleMeta] =
    queriesPayrollSchedules.updatePayrollSchedule.useMutation();

  const payrollSettings = queryPayerSettings.data?.payrollSettings;
  const schedule = querySchedules.data?.data?.[0];

  // The cadence resource is 501, so the stored frequency is usually unreadable.
  // The schedule dates it produced are readable, and inverting them recovers the
  // same cadence — see selectorPayrollCadence.
  const derivedCadence = selectorPayrollCadence(payrollSettings?.scheduleDates);

  const setRunsPayroll = useCallback(
    async (runsPayroll: boolean) => {
      await updatePayerSettings(
        { currentPayerSettingsUpdate: { payrollSettings: { runsPayroll } } },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: runsPayroll ? "Payroll enabled" : "Payroll disabled"
            });
          }
        }
      );
    },
    [openSnackbar, updatePayerSettings]
  );

  const handleSubmit = useCallback(
    async (data: FormData) => {
      // `updatePayrollSchedule` addresses an existing schedule by its own id, so
      // it works for the caller. `createPayrollSchedule` does not: its body
      // requires a `payerId`, and a payer Account has no Payer record naming
      // itself — the same defect PLA-3495 fixed for payer-settings, still open
      // for schedules. So a payer with no contractor schedule cannot make one.
      if (!schedule?.id) {
        openSnackbar({
          type: "warning",
          message: "No contractor payroll schedule exists to update yet"
        });
        return;
      }

      await updateSchedule(
        {
          payrollScheduleId: schedule.id,
          payrollScheduleUpdate:
            data.interval === Options.Week
              ? {
                  frequency: weekIntervalToFrequency(data.every),
                  firstPayDate: nextDateOnWeekDay(data.dayInInterval)
                }
              : {
                  frequency:
                    data.interval === Options.Everyday
                      ? PayrollScheduleFrequency.DAILY
                      : data.interval === Options.SemiMonthly
                        ? PayrollScheduleFrequency.SEMI_MONTHLY
                        : PayrollScheduleFrequency.MONTHLY
                }
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: "Payroll schedule updated"
            });
          }
        }
      );
    },
    [openSnackbar, schedule?.id, updateSchedule]
  );

  if (queryPayerSettings.isLoading) {
    return <WSLoader.Spinner />;
  }

  if (queryPayerSettings.error) {
    return (
      <ErrorAlert context="PayrollSettings" error={queryPayerSettings.error} />
    );
  }

  if (!payrollSettings?.runsPayroll) {
    return (
      <WSFlexBox direction="column" alignItems="center" alignContent="center">
        <EmptyState
          mb="M"
          image="NoResults"
          title="No upcoming payroll runs"
          description="Enable payroll to set up payroll runs"
        />

        <WSButton
          onClick={() => setRunsPayroll(true)}
          loading={updatePayerSettingsMeta.isLoading}
        >
          Enable payroll
        </WSButton>

        <ErrorAlert
          mt="M"
          context="PayrollSettings"
          error={updatePayerSettingsMeta.error}
        />
      </WSFlexBox>
    );
  }

  const upcomingRuns = (payrollSettings.scheduleDates || []).filter(
    scheduleDate =>
      scheduleDate.cutOffDate
        ? getExactCutOffDate(scheduleDate.cutOffDate) > new Date()
        : false
  );

  return (
    <WSList gap="2XL">
      {!payrollSettings.defaultFundingSource && (
        <WSAlert
          theme="warning"
          icon="alert"
          title="Funding method is missing"
          action={{
            text: "Add funding method",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD, {
                backPath: location.pathname
              });
            }
          }}
        >
          Please add a funding method to enable payroll
        </WSAlert>
      )}

      <WSForm<FormData>
        onSubmit={handleSubmit}
        defaultValues={frequencyToFormData(schedule, derivedCadence)}
        validationSchema={Yup.object().shape({
          interval: Yup.string().oneOf(Object.values(Options)).required(),
          every: Yup.number().when("interval", {
            is: Options.Week,
            then: Yup.number()
              .required("Required")
              .min(1, "Must be at least 1 week")
              .max(
                MAX_WEEK_INTERVAL,
                "Payroll can run every 1 or 2 weeks. Longer intervals aren't supported yet."
              )
          }),
          dayInInterval: Yup.string()
            .oneOf(Object.values(WeekDay))
            .when("interval", {
              is: Options.Week,
              then: Yup.string()
                .oneOf(Object.values(WeekDay))
                .required("Required")
            })
        })}
      >
        {({ setValue }) => (
          <WSList gap="XL">
            <WSElement>
              <WSText.ParagraphSm weight="medium">
                Expected pay date
              </WSText.ParagraphSm>

              {/* Only warn once both sources are gone. With the cadence resource
                  down but schedule dates present, the derivation gives the payer
                  their real setting and there is nothing to caveat; with neither,
                  the radios would otherwise present the "Monthly" fallback as
                  though it were saved, which is a confident lie. */}
              {!!querySchedules.error && !derivedCadence && (
                <WSAlert mt="M" theme="warning" icon="alert">
                  Your payroll cadence can't be loaded right now, so the
                  selection below isn't your saved setting. Saving will still
                  apply the cadence you choose.
                </WSAlert>
              )}

              <RadioGroupWithCustomInputs
                mt="M"
                label="How often should your contractors receive payments?"
                name="interval"
                options={[
                  { value: Options.Monthly, label: "Monthly" },
                  { value: Options.SemiMonthly, label: "Semi-monthly" },
                  { value: Options.Everyday, label: "Everyday" },
                  {
                    value: Options.Week,
                    label: (
                      <WSFlexBox wrap="nowrap" alignItems="center">
                        <WSText.ParagraphSm inline mr="M">
                          Every
                        </WSText.ParagraphSm>
                        {/* The inline fields sit inside a radio label, which
                            stretches to the row width — so each needs its own
                            fixed track or the two inputs span the whole page. */}
                        <WSElement style={{ width: 72, flex: "0 0 auto" }}>
                          <WSForm.Field
                            name="every"
                            component={WSInputNumber}
                            componentProps={{
                              min: 1,
                              max: MAX_WEEK_INTERVAL,
                              onFocus: () => setValue("interval", Options.Week)
                            }}
                          />
                        </WSElement>
                        <WSText.ParagraphSm inline mx="M">
                          week(s) on
                        </WSText.ParagraphSm>
                        <WSElement style={{ width: 180, flex: "0 0 auto" }}>
                          <WSForm.Field
                            name="dayInInterval"
                            component={WSSelect}
                            componentProps={{
                              options: WEEK_DAYS.map(day => ({
                                value: day,
                                label: day
                              })),
                              onFocus: () => setValue("interval", Options.Week)
                            }}
                          />
                        </WSElement>
                      </WSFlexBox>
                    )
                  }
                ]}
              />
            </WSElement>

            <ErrorAlert
              context="PayrollSettings"
              error={updateScheduleMeta.error}
            />
            <ErrorAlert
              context="PayrollSettings"
              error={updatePayerSettingsMeta.error}
            />

            <WSActions
              alignment="left"
              buttons={[
                {
                  label: "Save",
                  loading: updateScheduleMeta.isLoading
                },
                {
                  kind: "Secondary",
                  destructive: true,
                  type: "button",
                  label: "Disable payroll",
                  loading: updatePayerSettingsMeta.isLoading,
                  onClick: async () => {
                    if (await modalDisablePayroll.open({})) {
                      await setRunsPayroll(false);
                    }
                  }
                }
              ]}
            />
          </WSList>
        )}
      </WSForm>

      <WSElement style={{ maxWidth: 400 }}>
        <WSText.Heading5 mb="XL">Upcoming payroll runs</WSText.Heading5>

        <WSTable<PayerPayrollScheduleDate>
          tableData={upcomingRuns.map((scheduleDate, index) => ({
            id: String(index),
            data: scheduleDate
          }))}
          columns={[
            {
              config: { header: { text: "Processing date" } },
              renderFunction: ({ data }) => (
                <WSTableCell
                  text={toWSDateString(data.cutOffDate, "monthDayYear")}
                />
              )
            },
            {
              config: { header: { text: "Expected pay date" } },
              renderFunction: ({ data }) => (
                <WSTableCell text={toWSDateString(data.date, "monthDayYear")} />
              )
            }
          ]}
        />
      </WSElement>
    </WSList>
  );
};
